import { useEffect, useMemo, useRef, useState } from 'react';

import ModalShell from '../components/ModalShell.jsx';
import { formatTemperature } from '../dashboard/formatZone.js';
import {
  SI_STEP,
  SETPOINT_STEP,
  DEFAULT_SETPOINT,
  addStartRanges,
  addEndRanges,
  siDhwAddEndRanges,
  defaultEndTime,
  editStartRanges,
  editEndRanges,
  timeSelectOptions,
  clampSetpoint,
} from './scheduleEditModel.js';
import { isLastEventOfDay } from 'darwin_core/schedule/schedule.js';

// A modal editor for a single SI (boiler) schedule event. Heating events carry a
// setpoint (a 0.5 °C stepper clamped to the zone's min/max); hot-water events
// carry an On/Off state (a toggle). Both share start/end time selects whose
// options come straight from darwin_core's allowed-time ranges, so an edit can
// never produce an invalid slot. Save/Delete hand the chosen values back up — the
// screen turns them into a draft-schedule change. `value` is the darwin_core
// action field: { setpoint } for heating, { on } for hot water.
export default function EventEditorModal({
  mode,
  day,
  isDhw,
  working,
  is24Hour,
  minSetpoint,
  maxSetpoint,
  existing,
  canDelete,
  onSave,
  onDelete,
  onClose,
}) {
  const isEdit = mode === 'edit';
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const lastOfDay = useMemo(
    () =>
      isEdit && existing
        ? isLastEventOfDay([...working], day, existing.startTime)
        : false,
    [isEdit, existing, working, day],
  );

  // In add mode the initial start is the first available slot; the initial end is
  // its default — both computed synchronously so neither select ever renders a
  // frame with a value that matches no option.
  const [startTime, setStartTime] = useState(() => {
    if (isEdit) {
      return existing?.startTime;
    }
    const opts = timeSelectOptions(
      addStartRanges(working, day),
      SI_STEP,
      is24Hour,
    );
    return opts[0]?.value;
  });
  const [endTime, setEndTime] = useState(() => {
    if (isEdit) {
      return existing?.endTime;
    }
    const opts = timeSelectOptions(
      addStartRanges(working, day),
      SI_STEP,
      is24Hour,
    );
    const first = opts[0]?.value;
    return first === undefined
      ? undefined
      : defaultEndTime(working, day, first);
  });
  const [setpoint, setSetpoint] = useState(() =>
    clampSetpoint(
      isEdit && typeof existing?.setpoint === 'number'
        ? existing.setpoint
        : DEFAULT_SETPOINT,
      minSetpoint,
      maxSetpoint,
    ),
  );
  const [on, setOn] = useState(() =>
    isEdit && typeof existing?.on === 'boolean' ? existing.on : true,
  );

  // On a LATER start change (add mode) re-default the end, matching the app —
  // skip the first run since the initial end was already defaulted above.
  const firstRun = useRef(true);
  useEffect(() => {
    if (isEdit) {
      return;
    }
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    if (startTime !== undefined) {
      setEndTime(defaultEndTime(working, day, startTime));
    }
  }, [isEdit, startTime, working, day]);

  const startOptions = useMemo(() => {
    const ranges = isEdit
      ? editStartRanges(
          working,
          day,
          endTime,
          existing.startTime,
          existing.endTime,
        )
      : addStartRanges(working, day);
    return timeSelectOptions(ranges, SI_STEP, is24Hour);
  }, [isEdit, working, day, endTime, existing, is24Hour]);

  const endOptions = useMemo(() => {
    if (startTime === undefined) {
      return [];
    }
    let ranges;
    if (isEdit) {
      ranges = editEndRanges(
        working,
        day,
        startTime,
        existing.startTime,
        existing.endTime,
      );
    } else if (isDhw) {
      ranges = siDhwAddEndRanges(working, day, startTime);
    } else {
      ranges = addEndRanges(working, day, startTime);
    }
    return timeSelectOptions(ranges, SI_STEP, is24Hour);
  }, [isEdit, isDhw, working, day, startTime, existing, is24Hour]);

  const canSave = startTime !== undefined && endTime !== undefined;

  const step = (delta) =>
    setSetpoint((sp) => clampSetpoint(sp + delta, minSetpoint, maxSetpoint));

  const submit = () =>
    onSave({
      startTime,
      endTime,
      value: isDhw ? { on } : { setpoint },
    });

  const title = isEdit ? 'Edit event' : `Add event · ${day}`;

  return (
    <ModalShell labelledBy="schedule-modal-title" onClose={onClose}>
      <h2 id="schedule-modal-title" className="schedule-modal__title">
        {title}
      </h2>

      {isDhw ? (
        <div className="schedule-field">
          <span className="schedule-field__label">Hot water</span>
          <div className="onoff-toggle" role="group" aria-label="Hot water">
            <button
              type="button"
              className="onoff-toggle__btn"
              aria-pressed={on}
              onClick={() => setOn(true)}
            >
              On
            </button>
            <button
              type="button"
              className="onoff-toggle__btn"
              aria-pressed={!on}
              onClick={() => setOn(false)}
            >
              Off
            </button>
          </div>
        </div>
      ) : (
        <div className="schedule-field">
          <span className="schedule-field__label">Temperature</span>
          <div
            className="setpoint-stepper"
            role="group"
            aria-label="Temperature"
          >
            <button
              type="button"
              className="setpoint-stepper__btn"
              aria-label="Decrease temperature"
              disabled={setpoint <= minSetpoint}
              onClick={() => step(-SETPOINT_STEP)}
            >
              −
            </button>
            <span className="setpoint-stepper__value" aria-live="polite">
              {formatTemperature(setpoint) ?? '—'}
            </span>
            <button
              type="button"
              className="setpoint-stepper__btn"
              aria-label="Increase temperature"
              disabled={setpoint >= maxSetpoint}
              onClick={() => step(SETPOINT_STEP)}
            >
              +
            </button>
          </div>
        </div>
      )}

      <div className="schedule-field">
        <label className="schedule-field__label" htmlFor="event-start">
          Starts
        </label>
        <select
          id="event-start"
          className="schedule-field__select"
          value={startTime ?? ''}
          onChange={(e) => setStartTime(Number(e.target.value))}
        >
          {startOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="schedule-field">
        <label className="schedule-field__label" htmlFor="event-end">
          Ends
        </label>
        <select
          id="event-end"
          className="schedule-field__select"
          value={endTime ?? ''}
          disabled={lastOfDay || startTime === undefined}
          aria-describedby={lastOfDay ? 'event-end-hint' : undefined}
          onChange={(e) => setEndTime(Number(e.target.value))}
        >
          {endOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {lastOfDay && (
          <p id="event-end-hint" className="schedule-field__hint">
            The last event of the day runs until the next day begins.
          </p>
        )}
      </div>

      <div className="schedule-modal__actions">
        <button
          type="button"
          className="schedule-modal__save"
          disabled={!canSave}
          onClick={submit}
        >
          {isEdit ? 'Update event' : 'Add event'}
        </button>
        <button
          type="button"
          className="schedule-modal__cancel"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>

      {isEdit && canDelete && (
        <div className="schedule-modal__delete">
          {confirmingDelete ? (
            <div className="schedule-modal__confirm" role="group">
              <span>Delete this event?</span>
              <button
                type="button"
                className="schedule-modal__confirm-yes"
                onClick={onDelete}
              >
                Delete
              </button>
              <button
                type="button"
                className="schedule-modal__confirm-no"
                onClick={() => setConfirmingDelete(false)}
              >
                Keep
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="schedule-modal__delete-btn"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete event
            </button>
          )}
        </div>
      )}
    </ModalShell>
  );
}
